/**
 * Change shipping address: order number, the address as it stands, a form, a reason.
 *
 * The write is the Orders drawer's `ordersApi.editAddress`, same route, same compare and
 * set on the order version, same refusal once the carrier has the parcel. For a packer
 * that route asks for a tier 2 step-up; the API client opens the step-up dialog and
 * retries the save on its own, so this form never has to know.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Address } from '@elutide/shared';
import type { OrderDrawerDetailView as OrderDrawerDetail } from '@/features/orders/types';
import { Button, Card, ErrorState, Skeleton, TextArea, TextInput, useToast } from '@/components';
import { ApiProblem } from '@/lib/api';
import { ordersApi } from '@/features/orders/api';
import { orderDetailKey } from '@/features/orders/queries';
import { OrderFinder, OrderSummary } from './OrderFinder';
import type { BenchOrderRow } from './types';

const EMPTY_ADDRESS: Address = {
  first_name: '',
  last_name: '',
  company: null,
  address_1: '',
  address_2: null,
  city: '',
  state: '',
  postcode: '',
  country: 'US',
};

export function ChangeAddress() {
  const [order, setOrder] = useState<BenchOrderRow | null>(null);
  const orderId = order?.order_id ?? null;

  const detail = useQuery({
    queryKey: orderDetailKey(orderId ?? 0),
    queryFn: () => ordersApi.detail(orderId as number),
    enabled: orderId !== null,
  });

  return (
    <Card title="Change shipping address" id="bench-address">
      <p className="o-muted bench-lead">
        Fix a typo or a moved customer before the label prints. Once a label exists the
        address is locked here, and the label has to be voided first.
      </p>
      <OrderFinder idPrefix="address" onFound={setOrder} onCleared={() => setOrder(null)} />

      {order ? <OrderSummary order={order} /> : null}

      {order && detail.isLoading ? <Skeleton shape="card" /> : null}

      {order && detail.isError ? (
        <ErrorState
          message="The order did not load."
          requestId={detail.error instanceof ApiProblem ? detail.error.shortRequestId : null}
          onRetry={() => void detail.refetch()}
        />
      ) : null}

      {order && detail.data ? (
        <AddressForm
          key={`${detail.data.header.order_id}:${detail.data.header.version}`}
          detail={detail.data}
          onSaved={() => void detail.refetch()}
        />
      ) : null}
    </Card>
  );
}

function AddressBlock({ address }: { address: Address | null }) {
  if (!address) return <p className="o-muted">No shipping address on this order.</p>;
  const lines = [
    [address.first_name, address.last_name].filter(Boolean).join(' '),
    address.company,
    address.address_1,
    address.address_2,
    [address.city, address.state, address.postcode].filter(Boolean).join(' '),
    address.country,
  ].filter((line): line is string => Boolean(line && line.trim()));
  return (
    <address className="bench-address" data-testid="bench-address-current">
      {lines.map((line, index) => (
        <div key={`${index}-${line}`}>{line}</div>
      ))}
    </address>
  );
}

function AddressForm({ detail, onSaved }: { detail: OrderDrawerDetail; onSaved: () => void }) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [address, setAddress] = useState<Address>(detail.customer.shipping ?? EMPTY_ADDRESS);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    setAddress(detail.customer.shipping ?? EMPTY_ADDRESS);
  }, [detail]);

  const editable = detail.customer.address_editable;

  const field = (key: keyof Address, label: string, required = false) => (
    <TextInput
      label={label}
      required={required}
      autoComplete="off"
      value={(address[key] as string | null) ?? ''}
      onChange={(event) =>
        setAddress((current) => ({ ...current, [key]: event.target.value }) as Address)
      }
    />
  );

  async function save(): Promise<void> {
    setBusy(true);
    setProblem(null);
    try {
      await ordersApi.editAddress(detail.header.order_id, {
        expected_version: detail.header.version,
        shipping: address,
        reason: reason.trim(),
      });
      toast.push({ tone: 'ok', message: `Address updated on #${detail.header.order_number}.` });
      setEditing(false);
      setReason('');
      onSaved();
    } catch (caught) {
      setProblem(
        caught instanceof ApiProblem
          ? caught.shortRequestId
            ? `${caught.message} (request ${caught.shortRequestId})`
            : caught.message
          : 'The address did not save. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  const canSave =
    reason.trim().length > 0 &&
    address.first_name.trim() !== '' &&
    address.last_name.trim() !== '' &&
    address.address_1.trim() !== '' &&
    address.city.trim() !== '' &&
    address.state.trim() !== '' &&
    address.postcode.trim() !== '';

  return (
    <div className="bench-actions">
      <div className="o-field">
        <span className="o-field__label">Ships to</span>
        <AddressBlock address={detail.customer.shipping} />
      </div>

      {!editing ? (
        <div className="bench-actions__row">
          <Button variant="primary" disabled={!editable} onClick={() => setEditing(true)}>
            Change the address
          </Button>
          {!editable ? (
            <span className="o-muted bench-locked">
              A shipping label already exists for this order, so the address is locked.
            </span>
          ) : null}
        </div>
      ) : (
        <div className="bench-form">
          <div className="bench-form__grid">
            {field('first_name', 'First name', true)}
            {field('last_name', 'Last name', true)}
            {field('address_1', 'Address', true)}
            {field('address_2', 'Address line 2')}
            {field('city', 'City', true)}
            {field('state', 'State', true)}
            {field('postcode', 'ZIP', true)}
            {field('country', 'Country', true)}
          </div>
          <TextArea
            label="Why it is changing"
            hint="One short line. It goes in the order history."
            required
            rows={2}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          {problem ? (
            <p className="bench-problem" role="alert">
              {problem}
            </p>
          ) : null}
          <div className="bench-actions__row">
            <Button variant="primary" busy={busy} disabled={!canSave} onClick={() => void save()}>
              Save the address
            </Button>
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => {
                setEditing(false);
                setProblem(null);
                setAddress(detail.customer.shipping ?? EMPTY_ADDRESS);
              }}
            >
              Keep it as it was
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
